<script lang="ts">
	import { onMount, type Snippet } from 'svelte';
	import type { HTMLAttributes } from 'svelte/elements';
	import type { ButtonSize, GapSize } from './types';
	import { getPaddings } from './utils';
	import captureClick from '$lib/actions/captureClick';

	interface Props extends HTMLAttributes<HTMLButtonElement | HTMLAnchorElement> {
		iconLeading?: Snippet;
		iconTrailing?: Snippet;
		type?: HTMLButtonElement['type'];
		href?: HTMLAnchorElement['href'];
		target?: HTMLAnchorElement['target'];
		rel?: HTMLAnchorElement['rel'];
		size?: ButtonSize;
		gapSize?: GapSize;
		wrapperClass?: string;
		btn?: HTMLButtonElement | HTMLAnchorElement;
		disabled?: boolean;
	}

	let {
		iconLeading,
		iconTrailing,
		children,
		type = 'button',
		btn = $bindable(),
		class: className,
		size = 'large',
		gapSize = 'md',
		wrapperClass,
		href,
		target,
		rel,
		style,
		disabled = false,
		...other
	}: Props = $props();

	let iconLeadingEl = $state<HTMLSpanElement>();
	let iconTrailingEl = $state<HTMLSpanElement>();
	let hasIconLeading = $state(false);
	let hasIconTrailing = $state(false);

	const height: Record<ButtonSize, string> = {
		small: 'h-8',
		medium: 'h-10',
		large: 'h-12',
		'extra-large': 'h-14'
	};

	const scaleFactor = 0.1;

	let growFactor = $derived(1 + scaleFactor);
	let shrinkFactor = $derived(1 - scaleFactor);
	let scaleVars = $derived(`--grow-factor: ${growFactor};--shrink-factor: ${shrinkFactor}`);

	onMount(() => {
		hasIconLeading = iconLeadingEl !== undefined && iconLeadingEl.childNodes.length > 0;
		hasIconTrailing = iconTrailingEl !== undefined && iconTrailingEl.childNodes.length > 0;
	});

	let element = $derived(href ? 'a' : 'button') as 'a' | 'button';
	let isLink = $derived(element === 'a');

	let heightClass = $derived(height[size]);
	let paddingValues = $derived(getPaddings(size, hasIconLeading, hasIconTrailing));
	let paddingVars = $derived(
		`--padding-vertical: ${paddingValues.vertical}px; --padding-left: ${paddingValues.left}px;--padding-right: ${paddingValues.right}px`
	);
</script>

<div class="p-1 {wrapperClass || ''}" style={style || paddingVars}>
	<svelte:element
		this={element}
		use:captureClick
		bind:this={btn}
		{...other}
		{href}
		{target}
		{rel}
		type={isLink ? undefined : type}
		style={scaleVars}
		class={[
			heightClass,
			'btn-mill group/button flex origin-center transform-gpu items-center justify-center rounded-[32px] font-sans transition-transform duration-500 ease-in-out hover:scale-[var(--grow-factor)]',
			...(Array.isArray(className) ? className : [className]),
			{
				'cursor-pointer': isLink
			}
		]}
	>
		<span
			class={[
				'flex w-full origin-center transform-gpu items-center justify-center transition-transform duration-500 will-change-transform group-hover/button:scale-[var(--shrink-factor)]',
				{
					'gap-x-2': gapSize === 'md',
					'gap-x-1': gapSize === 'xs'
				}
			]}
		>
			{#if iconLeading}
				<span bind:this={iconLeadingEl}>{@render iconLeading()}</span>
			{/if}
			{@render children?.()}
			{#if iconTrailing}
				<span bind:this={iconTrailingEl}>{@render iconTrailing()}</span>
			{/if}
		</span>
	</svelte:element>
</div>

<style lang="scss">
	.btn-mill {
		padding: var(--padding-vertical) var(--padding-right) var(--padding-vertical)
			var(--padding-left);

		& > span {
			backface-visibility: hidden;
		}
	}
</style>
